import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card, Row, Col, Modal, Table } from "react-bootstrap";
import Breadcrumbs from "../../../../shared/components/Breadcrumbs";
import { ALLCATERGORIES } from "../../../../shared/constants/Constants";
import ServerSideGrid from "../../../../shared/components/listview/ServerSideGrid";
import DefaultButton from "../../../../shared/components/DefaultButton";
import { loadCategoryGetAllDataRequest, loadCategoryUpdateAllDataRequest, loadCategoryDeleteAllDataRequest } from "../redux/Actions";
import { loadCompanyDataRequest } from "../../../company/redux/Actions";

const AllCategories = () => {
  const dispatch = useDispatch();
  const { allcategories, loading, updateAllSuccess, updateAllError, deleteAllSuccess, deleteAllError } = useSelector((state) => state.categories);  const gridRef = useRef(null);
  const lastEditTypeRef = useRef(null); 
  const lastDeleteTypeRef = useRef(null);
  let {companies } = useSelector((state) => state.company);

  const [showModal, setShowModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [editSuccess, setEditSuccess] = useState(null);
  const [subModalMessage, setSubModalMessage] = useState({ text: "", type: "" });

  // ── Edit modal state ──
  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [editError, setEditError] = useState(null);
  //── delete modal state ──
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteData, setDeleteData] = useState(null);

  const handleSelectionChanged = useCallback(() => {
    const selected = gridRef.current?.getSelectedRows();
    setSelectedRow(selected?.length ? selected[0] : null);
  }, []);

  // Extract unique categories for dropdown filter
    const companyOptions = useMemo(() => {
      if (!companies) return [];
      return companies.map(c => ({ value: c.id, label: c.company_name }));
    }, [companies]);
    

    useEffect(() => {
      dispatch(loadCategoryGetAllDataRequest());
      dispatch(loadCompanyDataRequest());    
    }, [dispatch]);

  const rowData = useMemo(() => {
    if (!allcategories?.length) return [];
    return allcategories.map((cat) => ({
      id: cat.category_id,
      category_id: cat.category_id,
      category_name: cat.category_name,
      company_name: cat.company_name ?? companies?.find(c => c.id === cat.company_id)?.company_name ?? "",
      subcategories: cat.subcategories || [],
      subcategory_count: cat.subcategories?.length || 0,
      status: cat.is_active ? "Active" : "Inactive",
      
    }));
  }, [allcategories, companies]);

  useEffect(() => {
    if (!loading && rowData.length > 0) {
      const api = gridRef.current?.getApi();
      if (api) {
        api.setFilterModel({});
      }
    }
  }, [rowData]);

  const handleCategoryEdit = useCallback((data) => {
    setEditData({
      id: data.category_id,
      name: data.category_name,
      originalName: data.category_name,
      type: "category",
    });
    setShowEditModal(true);
  }, []);

  const handleSubEdit = useCallback((sub) => {
    setEditData({
      id: sub.subcategory_id,
      name: sub.subcategory_name,
      originalName: sub.subcategory_name,
      type: "subcategory",
    });
    setShowEditModal(true);
  }, []);

  const handleEditSave = useCallback(() => {
    if (!editData?.name?.trim()) return;
    setEditError(null);

    if (editData.name.trim() === editData.originalName?.trim()) {
      setShowEditModal(false);
      setEditData(null);
      return;
    }

    if (editData.type === "category") {
      const duplicate = allcategories.find(
        (cat) =>
          cat.category_name.toLowerCase().trim() === editData.name.toLowerCase().trim() &&
          cat.category_id !== editData.id
      );
      if (duplicate) {
        setEditError("A category with this name already exists!");
        return;
      }
    } else {
      const parentCategory = allcategories.find((cat) =>
        cat.subcategories?.some((sub) => sub.subcategory_id === editData.id)
      );
      const duplicate = parentCategory?.subcategories?.find(
        (sub) =>
          sub.subcategory_name.toLowerCase().trim() === editData.name.toLowerCase().trim() &&
          sub.subcategory_id !== editData.id
      );
      if (duplicate) {
        setEditError("A subcategory with this name already exists in this category!");
        return;
      }
    }

    lastEditTypeRef.current = editData.type; // ← save type before clearing
    dispatch(loadCategoryUpdateAllDataRequest({ id: editData.id, name: editData.name, type: editData.type }));
    setShowEditModal(false);
    setEditData(null);
  }, [dispatch, editData, allcategories]);

  const handleCategoryDelete = useCallback((id) => {
    const data = rowData.find((row) => row.id === id);
    const action = data?.status === "Active" ? "deactivate" : "activate";
    setDeleteData({ id, name: data?.category_name, action, type: "category" });
    setShowDeleteModal(true);
  }, [rowData]);

  const handleSubDelete = useCallback((sub) => {
    const action = sub.active ? "deactivate" : "activate";
    setDeleteData({ id: sub.subcategory_id, name: sub.subcategory_name, action, type: "subcategory" });
    setShowDeleteModal(true);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    lastDeleteTypeRef.current = deleteData.type;
    dispatch(loadCategoryDeleteAllDataRequest(deleteData.id, deleteData.action, deleteData.type)); // ← add deleteData.type
    setShowDeleteModal(false);
    setDeleteData(null);
  }, [dispatch, deleteData]);

  const columnDefs = useMemo(() => [
    {
      headerName: "Actions",
      field: "actions",
      width: 100,
      suppressFiltersToolPanel: true
    },
    {
      headerName: "Status",
      field: "status",
      filter: "agSetColumnFilter",
      sortable: true,
      width: 150,
      filterParams: { values: ["Active", "Inactive"] },
    },
    {
      headerName: "Company",
      field: "company_name",
      filter: "agSetColumnFilter",
      filterParams: {
        values: companyOptions.map(o => o.label),
      },
      sortable: true,
      width: 180,
    },
    {
      headerName: "Category",
      field: "category_name",
      filter: "agSetColumnFilter",
      sortable: true,
      width: 300,
    },
    {
      headerName: "Subcategories",
      field: "subcategories",
      sortable: true,
      width: 740,
      filter: "agSetColumnFilter",
      filterValueGetter: (params) =>
        params.data.subcategories?.map((sub) => sub.subcategory_name),
      valueFormatter: (params) =>
        params.value?.map((sub) => sub.subcategory_name).join(", ") || "",
      cellRenderer: (params) => (
        <span
          style={{ color: "#007bff", cursor: "pointer" }}
          onClick={() => {
            setSelectedCategory(params.data);
            setShowModal(true);
          }}
        >
          {params.value?.map((sub) => sub.subcategory_name).join(", ")}
        </span>
      ),
    },
  ], [companyOptions]);

  // ── Watch for API response — only for subcategory, using ref ──
  const showSubMessage = (text, type, ref) => {
    setSubModalMessage({ text, type });
    setTimeout(() => setSubModalMessage({ text: "", type: "" }), 3000);
    ref.current = null;
  };

  useEffect(() => {
    if (lastEditTypeRef.current === "subcategory") {
      if (updateAllSuccess) showSubMessage("Updated successfully!", "success", lastEditTypeRef);
      if (updateAllError) showSubMessage(typeof updateAllError === "string" ? updateAllError : "Could not update", "error", lastEditTypeRef);
    }
    if (lastDeleteTypeRef.current === "subcategory") {
      if (deleteAllSuccess) showSubMessage("Updated successfully!", "success", lastDeleteTypeRef);
      if (deleteAllError) showSubMessage(typeof deleteAllError === "string" ? deleteAllError : "Could not update", "error", lastDeleteTypeRef);
    }
  }, [updateAllSuccess, updateAllError, deleteAllSuccess, deleteAllError]);

  return (
    <>
      <Row className="detailsPageHeader">
        <Col>
          <Breadcrumbs children={[{ href: "/allcategories/", label: ALLCATERGORIES }]} />
        </Col>
      </Row>

      <Row>
        <Card className="table-card">
          <Card.Body>
            <ServerSideGrid
              ref={gridRef}
              clientSide={true}
              rowData={rowData}
              loading={loading}
              columnDefs={columnDefs}
              tableHeight="70vh"
              showPagination={true}
              paginationPageSize={80}
              theme="alpine"
              clickEdit={handleCategoryEdit}
              clickDelete={handleCategoryDelete}
              onSelectionChanged={handleSelectionChanged}
            />
          </Card.Body>
        </Card>
      </Row>

      {/* Subcategories Modal */}
      <Modal
        show={showModal}
        onHide={() => { setShowModal(false); setSubModalMessage({ text: "", type: "" }); }}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{selectedCategory?.category_name}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: "400px", overflowY: "auto" }}>
          {subModalMessage.text && (
            <div className={`alert ${subModalMessage.type === "success" ? "alert-success" : "alert-danger"} p-2 mb-2`}>
              {subModalMessage.text}
            </div>
          )}
          <Table bordered hover responsive size="sm">
            <thead style={{ backgroundColor: "#f8f9fa" }}>
              <tr>
                <th>Subcategory</th>
                <th>Status</th>
                <th style={{ width: "100px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {selectedCategory?.subcategories?.length ? (
                selectedCategory.subcategories.map((sub) => (
                  <tr key={sub.subcategory_id}>
                    <td>{sub.subcategory_name}</td>
                    <td>
                      <span style={{ color: sub.active ? "#198754" : "#dc3545", fontWeight: 600 }}>
                        {sub.active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td>
                      <span
                        className="edit-btn-icon ms-1 me-2"
                        style={{ cursor: "pointer" }}
                        onClick={() => handleSubEdit(sub)}
                      >
                        <i className="bx bxs-pencil"></i>
                      </span>
                      <span
                        className="delete-btn-icon"
                        style={{ cursor: "pointer" }}
                        onClick={() => handleSubDelete(sub)}
                      >
                        <i className="bx bxs-trash"></i>
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3} className="text-center text-muted">No subcategories found</td>
                </tr>
              )}
            </tbody>
          </Table>
        </Modal.Body>
      </Modal>

      {/* Edit Modal */}
      <Modal
        show={showEditModal}
        onHide={() => { setShowEditModal(false); setEditData(null); setEditError(null); }}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit {editData?.type === "category" ? "Category" : "Subcategory"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label">
              {editData?.type === "category" ? "Category" : "Subcategory"} Name
            </label>
            <input
              type="text"
              className="form-control"
              value={editData?.name || ""}
              onChange={(e) => {
                setEditError(null);
                setEditData((prev) => ({ ...prev, name: e.target.value }));
              }}
            />
            {editError && (
              <div className="alert alert-danger mt-2 p-2" role="alert">
                {editError}
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <DefaultButton
            buttonClass="btn-secondary btn-sm"
            buttonTitle="Cancel"
            actionBlock={() => { setShowEditModal(false); setEditData(null); setEditError(null); }}
          />
          <DefaultButton
            buttonClass="default-btn-color"
            buttonTitle="Save"
            actionBlock={handleEditSave}
            disabled={!editData?.name?.trim()}
          />
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => { setShowDeleteModal(false); setDeleteData(null); }}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm {deleteData?.action === "deactivate" ? "Deactivate" : "Activate"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to <strong>{deleteData?.action}</strong> the {deleteData?.type}{" "}
            <strong>{deleteData?.name}</strong>?
          </p>
        </Modal.Body>
        <Modal.Footer>
          <DefaultButton
            buttonClass="btn-secondary btn-sm"
            buttonTitle="Cancel"
            actionBlock={() => { setShowDeleteModal(false); setDeleteData(null); }}
          />
          <DefaultButton
            buttonClass="btn-danger btn-sm"
            buttonTitle={deleteData?.action === "deactivate" ? "Deactivate" : "Activate"}
            actionBlock={handleDeleteConfirm}
          />
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AllCategories;